/**
 * GA Storage - 学習結果の保存/読み込み
 *
 * 学習済みゲノムをlocalStorageに保存し、再利用可能にする。
 */

import {
  type AIGenome,
  DEFAULT_GENOME,
  serializeGenome,
  deserializeGenome,
  normalizeGenome,
} from './genome';
import type { EvolutionLog } from './trainer';

// =============================================================================
// 定数
// =============================================================================

/** ストレージキーのプレフィックス */
const STORAGE_PREFIX = 'magiSp_ai_genome_';

/** ゲノムリストのキー */
const GENOME_LIST_KEY = 'magiSp_ai_genome_list';

/** デフォルトゲノムのID */
export const DEFAULT_GENOME_ID = 'default';

// =============================================================================
// 型定義
// =============================================================================

/** 保存されるゲノムメタデータ */
export interface SavedGenome {
  id: string;
  name: string;
  genome: AIGenome;
  fitness: number;
  generations: number;
  createdAt: number;
  updatedAt: number;
  description?: string;
  evolutionLog?: EvolutionLog[];
}

/** ゲノムリストエントリ（軽量版） */
export interface GenomeListEntry {
  id: string;
  name: string;
  fitness: number;
  generations: number;
  createdAt: number;
  updatedAt: number;
  description?: string;
}

// =============================================================================
// ストレージ操作
// =============================================================================

/**
 * ゲノムを保存
 *
 * @param id - ゲノムID（ユニーク）
 * @param genome - 保存するゲノム
 * @param options - オプション
 * @returns 保存成功したか
 */
export function saveGenome(
  id: string,
  genome: AIGenome,
  options: {
    name?: string;
    fitness?: number;
    generations?: number;
    description?: string;
    evolutionLog?: EvolutionLog[];
  } = {}
): boolean {
  try {
    const now = Date.now();
    const existing = loadGenome(id);

    const saved: SavedGenome = {
      id,
      name: options.name || id,
      genome: normalizeGenome(genome),
      fitness: options.fitness || 0,
      generations: options.generations || 0,
      createdAt: existing?.createdAt || now,
      updatedAt: now,
      description: options.description,
      evolutionLog: options.evolutionLog,
    };

    // ゲノム本体を保存
    localStorage.setItem(
      STORAGE_PREFIX + id,
      JSON.stringify(saved)
    );

    // リストを更新
    updateGenomeList(id, saved);

    return true;
  } catch (error) {
    console.error('Failed to save genome:', error);
    return false;
  }
}

/**
 * ゲノムを読み込み
 *
 * @param id - ゲノムID
 * @returns 読み込んだゲノム（存在しない場合null）
 */
export function loadGenome(id: string): SavedGenome | null {
  try {
    // デフォルトゲノムの特別処理
    if (id === DEFAULT_GENOME_ID) {
      return {
        id: DEFAULT_GENOME_ID,
        name: 'Default (Hard AI)',
        genome: DEFAULT_GENOME,
        fitness: 0,
        generations: 0,
        createdAt: 0,
        updatedAt: 0,
        description: 'デフォルトのHard AI相当パラメータ',
      };
    }

    const json = localStorage.getItem(STORAGE_PREFIX + id);
    if (!json) return null;

    const saved = JSON.parse(json) as SavedGenome;
    saved.genome = normalizeGenome(saved.genome);
    return saved;
  } catch (error) {
    console.error('Failed to load genome:', error);
    return null;
  }
}

/**
 * ゲノムを削除
 *
 * @param id - ゲノムID
 * @returns 削除成功したか
 */
export function deleteGenome(id: string): boolean {
  try {
    if (id === DEFAULT_GENOME_ID) {
      console.warn('Cannot delete default genome');
      return false;
    }

    localStorage.removeItem(STORAGE_PREFIX + id);
    removeFromGenomeList(id);
    return true;
  } catch (error) {
    console.error('Failed to delete genome:', error);
    return false;
  }
}

/**
 * 全ゲノムを削除
 */
export function clearAllGenomes(): void {
  try {
    const list = getGenomeList();
    for (const entry of list) {
      if (entry.id !== DEFAULT_GENOME_ID) {
        localStorage.removeItem(STORAGE_PREFIX + entry.id);
      }
    }
    localStorage.removeItem(GENOME_LIST_KEY);
  } catch (error) {
    console.error('Failed to clear genomes:', error);
  }
}

// =============================================================================
// ゲノムリスト管理
// =============================================================================

/**
 * ゲノムリストを取得
 */
export function getGenomeList(): GenomeListEntry[] {
  try {
    const json = localStorage.getItem(GENOME_LIST_KEY);
    if (!json) return [];
    return JSON.parse(json) as GenomeListEntry[];
  } catch (error) {
    console.error('Failed to get genome list:', error);
    return [];
  }
}

/**
 * ゲノムリストを更新
 */
function updateGenomeList(id: string, saved: SavedGenome): void {
  const list = getGenomeList();
  const entry: GenomeListEntry = {
    id: saved.id,
    name: saved.name,
    fitness: saved.fitness,
    generations: saved.generations,
    createdAt: saved.createdAt,
    updatedAt: saved.updatedAt,
    description: saved.description,
  };

  const idx = list.findIndex(e => e.id === id);
  if (idx >= 0) {
    list[idx] = entry;
  } else {
    list.push(entry);
  }

  // 適応度順にソート
  list.sort((a, b) => b.fitness - a.fitness);

  localStorage.setItem(GENOME_LIST_KEY, JSON.stringify(list));
}

/**
 * ゲノムリストから削除
 */
function removeFromGenomeList(id: string): void {
  const list = getGenomeList();
  const filtered = list.filter(e => e.id !== id);
  localStorage.setItem(GENOME_LIST_KEY, JSON.stringify(filtered));
}

// =============================================================================
// エクスポート/インポート
// =============================================================================

/**
 * ゲノムをJSONにエクスポート
 */
export function exportGenome(id: string): string | null {
  const saved = loadGenome(id);
  if (!saved) return null;
  return JSON.stringify(saved, null, 2);
}

/**
 * JSONからゲノムをインポート
 */
export function importGenome(json: string, newId?: string): boolean {
  try {
    const saved = JSON.parse(json) as SavedGenome;

    // 新しいIDが指定された場合は上書き
    if (newId) {
      saved.id = newId;
    }

    // 既存と重複する場合はIDに接尾辞を追加
    let targetId = saved.id;
    let suffix = 1;
    while (loadGenome(targetId) !== null && targetId !== DEFAULT_GENOME_ID) {
      targetId = `${saved.id}_${suffix}`;
      suffix++;
    }

    return saveGenome(targetId, saved.genome, {
      name: saved.name,
      fitness: saved.fitness,
      generations: saved.generations,
      description: saved.description,
      evolutionLog: saved.evolutionLog,
    });
  } catch (error) {
    console.error('Failed to import genome:', error);
    return false;
  }
}

/**
 * 全ゲノムをJSONにエクスポート
 */
export function exportAllGenomes(): string {
  const list = getGenomeList();
  const genomes: SavedGenome[] = [];

  for (const entry of list) {
    const saved = loadGenome(entry.id);
    if (saved) {
      genomes.push(saved);
    }
  }

  return JSON.stringify(genomes, null, 2);
}

/**
 * JSONから複数ゲノムをインポート
 */
export function importAllGenomes(json: string): number {
  try {
    const genomes = JSON.parse(json) as SavedGenome[];
    let imported = 0;

    for (const saved of genomes) {
      if (importGenome(JSON.stringify(saved))) {
        imported++;
      }
    }

    return imported;
  } catch (error) {
    console.error('Failed to import genomes:', error);
    return 0;
  }
}

// =============================================================================
// ユーティリティ
// =============================================================================

/**
 * 最も適応度の高いゲノムを取得
 */
export function getBestGenome(): SavedGenome | null {
  const list = getGenomeList();
  if (list.length === 0) return null;

  // リストは適応度順にソート済み
  return loadGenome(list[0].id);
}

/**
 * ゲノムが存在するか確認
 */
export function hasGenome(id: string): boolean {
  if (id === DEFAULT_GENOME_ID) return true;
  return localStorage.getItem(STORAGE_PREFIX + id) !== null;
}

/**
 * ユニークなゲノムIDを生成
 */
export function generateGenomeId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substr(2, 5);
  return `genome_${timestamp}_${random}`;
}
